import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { canShowLqCollection } from '../../util';
import AppTheme from '../../styles/AppTheme';
import CssBaseline from '@mui/material/CssBaseline';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';

const API_BASE = 'https://api.fmb52.com';
const PDC_CHEQUE_CUTOFF = new Date('2026-05-01').getTime();

function isPdcCheque(row) {
  if ((row.mode || '').toLowerCase() !== 'cheque') return false;
  const raw = row.cheque_date;
  if (!raw) return false;
  const d = new Date(raw);
  return !isNaN(d.getTime()) && d.getTime() > PDC_CHEQUE_CUTOFF;
}

export default function LQCollection() {
  const { user, token, currency } = useUser();
  const outletContext = useOutletContext() || {};
  const selectedYear = outletContext.selectedYear;
  const yearFromContext = Array.isArray(selectedYear) ? selectedYear[0] : selectedYear;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    entries: [],
    receipts: [],
    totals: { cash: 0, cheque: 0, neft: 0 },
    user_wise_totals: {},
  });
  const [year] = useState(yearFromContext || '');
  const [mode, setMode] = useState('');
  const [collectedByFilter, setCollectedByFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const canAccess = canShowLqCollection(user);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboard', { replace: true });
      return;
    }
  }, [canAccess, navigate]);

  const activeYear = yearFromContext || year;

  useEffect(() => {
    document.title = 'LQ Collection - FMB 52';
  }, []);

  const fetchData = useCallback(async () => {
    if (!token || !canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeYear) params.set('year', activeYear);
      if (mode) params.set('mode', mode);
      const url = `${API_BASE}/api/receipts/advance-receipts${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.message || 'Failed to load advance receipts');
        setData({ entries: [], receipts: [], totals: { cash: 0, cheque: 0, neft: 0 }, user_wise_totals: {} });
        return;
      }
      setData({
        entries: json?.data?.entries ?? [],
        receipts: json?.data?.receipts ?? [],
        totals: json?.data?.totals ?? { cash: 0, cheque: 0, neft: 0 },
        user_wise_totals: json?.data?.user_wise_totals ?? {},
      });
    } catch (e) {
      setError('An error occurred while loading data');
      setData({ entries: [], receipts: [], totals: { cash: 0, cheque: 0, neft: 0 }, user_wise_totals: {} });
    } finally {
      setLoading(false);
    }
  }, [token, canAccess, activeYear, mode]);

  useEffect(() => {
    if (canAccess && token) fetchData();
  }, [canAccess, token, fetchData]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency?.currency_code || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value) || 0);

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const date = new Date(d);
      return isNaN(date.getTime()) ? d : date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return d;
    }
  };

  const [printLoading, setPrintLoading] = useState(false);

  const handlePrint = useCallback(async () => {
    if (!token) return;
    setPrintLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeYear) params.set('year', activeYear);
      if (mode) params.set('mode', mode);
      if (collectedByFilter) params.set('collected_by', collectedByFilter);
      const url = `${API_BASE}/api/receipts/advance-receipts/pdf${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err?.message || 'Failed to load PDF');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setError('Failed to generate PDF');
    } finally {
      setPrintLoading(false);
    }
  }, [token, activeYear, mode, collectedByFilter]);

  const rawEntries = data.entries || [];
  const rawReceipts = data.receipts || [];
  const userWiseTotals = data.user_wise_totals || {};

  const byCollector = (rows, logUserKey = 'log_user') =>
    !collectedByFilter
      ? rows
      : rows.filter((r) => (r[logUserKey] || 'unknown') === collectedByFilter);

  const entriesByCollector = byCollector(rawEntries);
  const receiptsByCollector = byCollector(rawReceipts);

  const advanceTotalsFiltered =
    collectedByFilter
      ? entriesByCollector.reduce(
          (acc, r) => {
            const m = (r.mode || '').toLowerCase();
            const amt = Number(r.amount) || 0;
            if (m === 'cash') acc.cash += amt;
            else if (m === 'cheque') acc.cheque += amt;
            else if (m === 'neft') acc.neft += amt;
            return acc;
          },
          { cash: 0, cheque: 0, neft: 0 }
        )
      : null;

  const totals = data.totals || { cash: 0, cheque: 0, neft: 0 };
  const effectiveAdvanceTotals = advanceTotalsFiltered ?? totals;
  const receiptTotals = receiptsByCollector.reduce(
    (acc, r) => {
      const m = (r.mode || '').toLowerCase();
      const amt = Number(r.amount) || 0;
      if (m === 'cash') acc.cash += amt;
      else if (m === 'cheque') acc.cheque += amt;
      else if (m === 'neft') acc.neft += amt;
      return acc;
    },
    { cash: 0, cheque: 0, neft: 0 }
  );
  const combinedCash = (Number(effectiveAdvanceTotals.cash) || 0) + (Number(receiptTotals.cash) || 0);
  const combinedCheque = (Number(effectiveAdvanceTotals.cheque) || 0) + (Number(receiptTotals.cheque) || 0);
  const combinedNeft = (Number(effectiveAdvanceTotals.neft) || 0) + (Number(receiptTotals.neft) || 0);
  const pdcChequeTotal = [...entriesByCollector, ...receiptsByCollector]
    .filter(isPdcCheque)
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  // Total Amount includes PDC: Cash + Cheque (non-PDC) + PDC Cheque + NEFT
  const chequeNonPdc = Math.max(0, combinedCheque - pdcChequeTotal);
  const totalAmount = combinedCash + chequeNonPdc + pdcChequeTotal + combinedNeft;
  const totalNiyaz = Math.floor(totalAmount / 172000);
  const uniqueFamilyIds = new Set([
    ...(entriesByCollector.map((r) => r.family_id).filter(Boolean)),
    ...(receiptsByCollector.map((r) => r.family_id).filter(Boolean)),
  ]);
  const mumineenContributed = uniqueFamilyIds.size;

  const searchLower = (searchText || '').trim().toLowerCase();
  const entries = searchLower
    ? entriesByCollector.filter(
        (row) =>
          (row.name && String(row.name).toLowerCase().includes(searchLower)) ||
          (row.log_user && String(row.log_user).toLowerCase().includes(searchLower)) ||
          (row.its && String(row.its).toLowerCase().includes(searchLower))
      )
    : entriesByCollector;
  const receipts = searchLower
    ? receiptsByCollector.filter(
        (row) =>
          (row.name && String(row.name).toLowerCase().includes(searchLower)) ||
          (row.log_user && String(row.log_user).toLowerCase().includes(searchLower)) ||
          (row.its && String(row.its).toLowerCase().includes(searchLower)) ||
          (row.receipt_no && String(row.receipt_no).toLowerCase().includes(searchLower))
      )
    : receiptsByCollector;

  const collectorOptions = [
    { value: '', label: 'All' },
    ...Object.entries(userWiseTotals)
      .filter(([its]) => its !== 'unknown' || (typeof userWiseTotals[its] === 'object' && userWiseTotals[its]?.total > 0))
      .map(([its, v]) => ({
        value: its,
        label: (typeof v === 'object' && v !== null && v.name) ? v.name : its,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })),
  ];

  if (!canAccess) return null;

  return (
    <AppTheme>
      <CssBaseline />
      <Box
        className="lq-collection-print"
        sx={{
          mt: 11,
          pt: 1,
          px: 2,
          pb: 4,
          '@media print': {
            mt: 0,
            p: 2,
            '& .no-print': { display: 'none !important' },
            '& .print-only': { display: 'block !important' },
          },
        }}
      >
        <Typography
          className="print-only"
          variant="h4"
          sx={{
            display: 'none',
            mb: 2,
            fontWeight: 700,
            '@media print': { display: 'block !important', marginBottom: 2 },
          }}
        >
          LQ Collection — Advance Receipts & Receipts Report
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }} className="no-print">
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: '1.35rem' }}>
            LQ Collection
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search (ITS / Name)"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by ITS or name"
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Mode</InputLabel>
              <Select value={mode} onChange={(e) => setMode(e.target.value)} label="Mode">
                <MenuItem value="">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="neft">NEFT</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Collected by</InputLabel>
              <Select
                value={collectedByFilter}
                onChange={(e) => setCollectedByFilter(e.target.value)}
                label="Collected by"
              >
                {collectorOptions.map((opt) => (
                  <MenuItem key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={fetchData} disabled={loading} startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              onClick={handlePrint}
              disabled={printLoading}
              startIcon={printLoading ? <CircularProgress size={18} /> : <PrintIcon />}
            >
              {printLoading ? 'Loading…' : 'Print'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} className="no-print">
            {error}
          </Alert>
        )}

        <Box sx={{ '@media print': { breakInside: 'avoid' } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600, '@media print': { fontSize: '1rem' } }}>
            Summary
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 1,
              mb: 2,
              overflowX: 'auto',
              minWidth: 0,
              '& > .summary-card': { flex: '1 1 0', minWidth: 0 },
            }}
          >
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Mumineen Contributed</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{mumineenContributed}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Total Amount</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{formatCurrency(totalAmount)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Cash</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{formatCurrency(combinedCash)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Cheque</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{formatCurrency(chequeNonPdc)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>PDC Cheque</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{formatCurrency(pdcChequeTotal)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>NEFT</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{formatCurrency(combinedNeft)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" className="summary-card" sx={{ minWidth: 0 }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>Niyaz</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '1.05rem' }}>{totalNiyaz}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ '@media print': { breakInside: 'avoid' } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600, '@media print': { fontSize: '1rem' } }}>
            Advance Receipts
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, '@media print': { boxShadow: 'none' } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>SN</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Year</strong></TableCell>
                  <TableCell><strong>Mode</strong></TableCell>
                  <TableCell><strong>Collected by</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={7} align="center">No advance receipts found</TableCell></TableRow>
                )}
                {entries.map((row, idx) => {
                  const uw = userWiseTotals[row.log_user];
                  const resolvedName = (typeof uw === 'object' && uw !== null && uw.name) ? uw.name : null;
                  const displayName =
                    row.log_user_name ?? row.collector_name ?? row.collected_by_name ?? resolvedName ?? row.log_user ?? '—';
                  return (
                    <TableRow key={row.id || idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.name ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(row.amount)}</TableCell>
                      <TableCell>{row.year ?? '—'}</TableCell>
                      <TableCell>{row.mode ?? '—'}</TableCell>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>{formatDate(row.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ '@media print': { breakInside: 'avoid' } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600, '@media print': { fontSize: '1rem' } }}>
            Receipts
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, '@media print': { boxShadow: 'none' } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>SN</strong></TableCell>
                  <TableCell><strong>Receipt No</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Year</strong></TableCell>
                  <TableCell><strong>Mode</strong></TableCell>
                  <TableCell><strong>Collected by</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={8} align="center">No receipts found</TableCell></TableRow>
                )}
                {receipts.map((row, idx) => {
                  const uw = userWiseTotals[row.log_user];
                  const resolvedName = (typeof uw === 'object' && uw !== null && uw.name) ? uw.name : null;
                  const displayName =
                    row.collected_by ?? row.collector_name ?? resolvedName ?? row.log_user ?? '—';
                  return (
                    <TableRow key={row.id ?? row.hashed_id ?? idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.receipt_no ?? '—'}</TableCell>
                      <TableCell>{row.name ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(row.amount)}</TableCell>
                      <TableCell>{row.year ?? '—'}</TableCell>
                      <TableCell>{row.mode ?? '—'}</TableCell>
                      <TableCell>{displayName}</TableCell>
                      <TableCell>{formatDate(row.date ?? row.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ '@media print': { breakInside: 'avoid' } }}>
          <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem', fontWeight: 600, '@media print': { fontSize: '1rem' } }}>
            User-wise Totals
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ '@media print': { boxShadow: 'none' } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ITS</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>Cash</strong></TableCell>
                  <TableCell align="right"><strong>Cheque</strong></TableCell>
                  <TableCell align="right"><strong>NEFT</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(userWiseTotals).length === 0 && !loading && (
                  <TableRow><TableCell colSpan={6} align="center">No user-wise data</TableCell></TableRow>
                )}
                {Object.entries(userWiseTotals).map(([its, v]) => {
                  const isObj = typeof v === 'object' && v !== null;
                  const total = isObj && 'total' in v ? Number(v.total) : Number(v);
                  const cash = isObj && 'cash' in v ? Number(v.cash) : 0;
                  const cheque = isObj && 'cheque' in v ? Number(v.cheque) : 0;
                  const neft = isObj && 'neft' in v ? Number(v.neft) : 0;
                  const name = isObj && 'name' in v ? v.name : null;
                  if (its === 'unknown' && total === 0 && cash === 0 && cheque === 0 && neft === 0) return null;
                  return (
                    <TableRow key={its}>
                      <TableCell>{its}</TableCell>
                      <TableCell>{name ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(total)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(cash)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(cheque)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 500 }}>{formatCurrency(neft)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mt: 1.5, textAlign: 'center', '@media print': { marginTop: 2 } }} className="no-print">
          <Typography variant="caption" color="text.secondary">
            LQ Collection — Advance receipts & receipts. Visible only to authorised users.
          </Typography>
        </Box>
      </Box>

      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .lq-collection-print { max-width: 100%; }
          .MuiTable-root { page-break-inside: auto; }
          .MuiTableRow-root { page-break-inside: avoid; }
        }
      `}</style>
    </AppTheme>
  );
}
